<template>
  <section :class="{'dark-mode': useDarkMode && togglerShow}">
    <parallax :from="{ y: -100 }" :to="{ y: 0 }">
      <div slot-scope="{ parallax }">
          <lazy-image :className="['toggler-item', imageStart, { [imageHide] : togglerShow }]" :item="image" :sizes="sizes"></lazy-image>
          <lazy-image :className="['toggler-item', togglerStart, { [togglerHide] : togglerShow}]" :item="toggledImage" :sizes="sizes"></lazy-image>
        </div>
    </parallax>
    <button type="button" ref="button" class="h5"><span><i class="icon-digital"></i></span>{{ $t('Click & hold') }}</button>
  </section>
</template>

<script>
import { Parallax } from 'leaps/dist/leaps'
import { ReadyHook } from 'vue-asyncready'
import ImageManager from 'vue-storefront-prismic-ssr/utilities/ImageManager'
import LazyImage from './utils/LazyImage'

export default {

  props: ['item', 'items', 'pagePosition', 'siblings-components'],
  name: 'image_toggler',
  mixins: [ReadyHook],
  components: {
    Parallax,
    LazyImage
  },
  data () {
    return {
      intervalID: null,
      togglerShow: false,
      useDarkMode: false
    }
  },
  mounted () {
    this.$refs.button.addEventListener('mouseup', this.pressBtn)
    this.$refs.button.addEventListener('mousedown', this.pressBtn)
    this.$refs.button.addEventListener('touchstart', this.pressBtn)
    this.$refs.button.addEventListener('touchend', this.pressBtn)
  },
  methods: {
    pressBtn (e) {
      (e.type === 'mousedown' || e.type === 'touchstart' ? this.holdIn() : this.holdOut())
    },
    holdIn () {
      this.intervalID = setTimeout(() => {
        this.togglerShow = true
      }, 200)
    },
    holdOut () {
      clearTimeout(this.intervalID)
      this.intervalID = null
      setTimeout(() => {
        this.togglerShow = false
      }, 200)
    }
  },
  destroyed: function () {
    window.removeEventListener('mouseup', this.pressBtn)
    window.removeEventListener('mousedown', this.pressBtn)
    window.removeEventListener('touchstart', this.pressBtn)
    window.removeEventListener('touchend', this.pressBtn)
  },
  computed: {
    imageStart() {
      return (this.appDarkMode && this.useDarkMode) ? 'toggler' : 'start'
    },
    togglerStart() {
      return (this.appDarkMode && this.useDarkMode) ? 'start' : 'toggler'
    },
    imageHide() {
      return (this.appDarkMode && this.useDarkMode) ? '-show' : '-hide'
    },
    togglerHide() {
      return (this.appDarkMode && this.useDarkMode) ? '-hide' : '-show'
    },
    togglerHidden() {
      return true
    },
    appDarkMode() {
      return this.$store.state.darkmode.activated
    },
    image() {
      return {
        url: this.item.image.url,
        lowDefinitionUrl: ImageManager.getLowDefinition(this.item.image.url, 200, 1, true, 4),
        srcset: ImageManager.getSrcSet(this.item.image.url, 1200, 100, 100, 1),
        alt: this.item.image.alt ? this.item.image.url : ''
      }
    },
    toggledImage() {
      return {
        url: this.item.toggle.url,
        lowDefinitionUrl: ImageManager.getLowDefinition(this.item.toggle.url, 200, 1, true, 4),
        srcset: ImageManager.getSrcSet(this.item.toggle.url, 1200, 100, 100, 1),
        alt: this.item.toggle.alt ? this.item.toggle.url : ''
      }
    },
    sizes() {
      return '(min-width: 768px) 92vw, (min-width: 992px) 71vw, (min-width: 1200px) 60vw, (min-width: 1600px) 44vw, 100vw'
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~theme/css/core";

section {
  position: relative;
  display: flex;
  padding: 0 0 15rem;
  overflow: hidden;
  text-align: center;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  background: var( --zenith-component-image-toggler-default);
  height: auto;
  transition: background-color .8s ease-in-out;
  transform-style: preserve-3d;

  @include bp(md) {
    height: calc(100vh - 12rem);
  }

  &.dark-mode {
    background-color: var(--zenith-component-image-toggler-pressed);

    button {
      color: var(--zenith-component-title);
    }
  }

  @include bp(max md) {
    padding: 0 0 10rem;
  }

  div {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .toggler-item {
    width: 100%;
    height: 100%;

    @include bp(md) {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    @include bp(md) {
      width: 92%;
    }
    @include bp(lg) {
      width: 71%;
    }
    @include bp(xl) {
      width: 60%;
    }
    @include bp(xxl) {
      width: 44%;
    }

    &.toggler {
      opacity: 0;
      transition: all .4s ease-in-out;

      @include bp(max md) {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
      }

      &.-show {
        opacity: 1;
        transition: all .8s ease-in-out;
      }
    }
    &.start {
      opacity: 1;
      transition: all .4s ease-in-out;

      &.-hide {
        opacity: 0;
        transition: all .8s ease-in-out;
      }
    }

    /deep/ img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  button {
    position: absolute;
    bottom: 10rem;
    color: var(--zenith-theme-primary);
    text-transform: uppercase;
    background: none;
    border: none;
    outline: none;
    -webkit-user-select: none; /* Safari */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* IE10+/Edge */
    user-select: none; /* Standard */

    @include bp(max md) {
      bottom: 4rem;
    }

    span {
      position: relative;
      display: block;
      width: 6rem;
      height: 6rem;
      margin: 0 auto 2rem;
      background: var(--zenith-white);
      border-radius: 100%;
      transition: all 0.2s ease-in-out;
      box-shadow: -1px 0 1px 0 rgba(0, 0, 0, 0.3), 1px 0 1px 0 rgba(0, 0, 0, 0.3);

      i {
        display: none;
        position: absolute;
        top: 50%;
        left: 50%;
        color: var(--zenith-theme-primary);
        font-size: em(34, 14);
        transform: translate(-48%, -52%);

        @include bp(max lg) {
          display: block;
        }
      }

      &:before {
        z-index: 10;
        content: '';
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        width: 2rem;
        height: 2rem;
        border-radius: 100%;
        border: 1px solid var(--zenith-theme-primary);
        transform: translate(-50%, -50%);

        @include bp(max lg) {
          display: none;
        }
      }

      &:after {
        z-index: 10;
        content: '';
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        width: 4rem;
        height: 4rem;
        border-radius: 100%;
        border: 1px solid var(--zenith-theme-primary);
        transform: translate(-50%, -50%);

        @include bp(max lg) {
          display: none;
        }
      }
    }

    &:hover {
      span {
        &:before {
          animation: zoom1 1s infinite;
        }
        &:after {
          animation: zoom2 1s infinite;
        }
      }
      &:focus {
        span {
          transform: scale(1.2);
        }
      }
    }
  }
}

@keyframes zoom1 {
  0%, 20%, 80%, 100% {
    width: 2rem;
    height: 2rem;
  }
  50% {
    width: 2.4rem;
    height: 2.4rem;
  }
}

@keyframes zoom2 {
  0%, 100% {
    width: 4rem;
    height: 4rem;
  }
  50% {
    width: 4.4rem;
    height: 4.4rem;
  }
}

</style>
